import React from "react";
import { Bell, CheckCircle2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { MarkedDay } from "@/features/app-context/types";
import type { PendingBreakfastInvite } from "@/features/breakfast/types";
import type { DBNotification } from "../types";
import { AttendanceCard } from "./AttendanceCard";
import { BreakfastInviteCard } from "./BreakfastInviteCard";
import { NotifRow } from "./NotifRow";
import type { NotifPanelActions, NotifPanelLoading } from "./panel-types";
import type { SenderMap } from "../hooks/useNotifSenders";

interface NotificationPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasPending: boolean;
  badgeCount: number;
  /** Triggers the bell swing keyframes when a new notification lands. */
  hasRung: boolean;
  /** Called after the bell-swing animation finishes to reset the flag. */
  onBellSwingEnd: () => void;
  /**
   * Opens the full notification sheet. Accepts an optional `notifId` so
   * clicking a specific DB row inside the popover preview can open the
   * sheet focused on that exact notification (deep-link semantics).
   */
  onOpenFullSheet: (notifId?: string) => void;

  /** Top-4 lists budgeted across the three lanes by the orchestrator. */
  popInvites: PendingBreakfastInvite[];
  popAtt:     MarkedDay[];
  popDb:      DBNotification[];

  /** IDs of pending invites that should render with the "Rescheduled" chip. */
  rescheduledEventIds: Set<string>;

  /** Resolved profile rows keyed by `notif.sender_id`. */
  senders: SenderMap;

  actions: NotifPanelActions;
  loading: NotifPanelLoading;
}

/**
 * Bell trigger + popover preview panel.
 *
 * Renders the always-visible bell button (with unread badge and
 * swing animation) and, on click, a compact 340-px popover listing at
 * most four items prioritised across three lanes:
 *
 *   1. Pending breakfast invites   (block scheduled events)
 *   2. Attendance confirmations    (past-day audits)
 *   3. Unread DB notifications     (everything else)
 *
 * When all three lanes are empty the popover renders a cheerful
 * "You're all caught up!" state.
 *
 * The footer links into the full Notification Centre sheet; the
 * `onMouseDown` (instead of `onClick`) on that link is deliberate — it
 * lets us defer sheet mount until after the popover has begun closing,
 * which prevents a focus-trap conflict in Radix.
 */
export function NotificationPopover({
  open,
  onOpenChange,
  hasPending,
  badgeCount,
  hasRung,
  onBellSwingEnd,
  onOpenFullSheet,
  popInvites,
  popAtt,
  popDb,
  rescheduledEventIds,
  senders,
  actions,
  loading,
}: NotificationPopoverProps) {
  const popoverIsEmpty =
    popAtt.length === 0 && popDb.length === 0 && popInvites.length === 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          aria-label={
            hasPending
              ? `${badgeCount} notification${badgeCount > 1 ? "s" : ""}`
              : "Notifications"
          }
          className={cn(
            "relative p-2 rounded-xl transition-colors no-select tap-highlight",
            hasPending
              ? "text-foreground hover:bg-accent"
              : "text-muted-foreground hover:text-foreground hover:bg-accent",
          )}
        >
          <Bell
            className={cn("h-5 w-5 origin-top", hasRung && "bell-swing")}
            onAnimationEnd={onBellSwingEnd}
          />
          {hasPending && (
            <span className="absolute -top-0.5 -right-0.5">
              <span className="absolute inset-0 rounded-full bg-destructive animate-ping opacity-70" />
              <span className="relative flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-destructive text-[9px] font-bold text-white leading-none">
                {badgeCount > 9 ? "9+" : badgeCount}
              </span>
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[340px] p-0 overflow-hidden shadow-xl"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-border/60 bg-muted/30 flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary flex-shrink-0" />
          <p className="text-sm font-semibold flex-1">Notifications</p>
          {hasPending && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {badgeCount} new
            </span>
          )}
        </div>

        {/* Items */}
        <div className="max-h-[320px] overflow-y-auto divide-y divide-border/40">
          {popoverIsEmpty ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <div className="h-11 w-11 rounded-2xl bg-green-500/10 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-sm font-semibold">You're all caught up!</p>
              <p className="text-xs text-muted-foreground mt-1">
                No new notifications.
              </p>
            </div>
          ) : (
            <>
              {popInvites.map((invite) => (
                <BreakfastInviteCard
                  key={invite.id}
                  invite={invite}
                  compact
                  rescheduled={rescheduledEventIds.has(invite.id)}
                  isLoading={loading.breakfastInviteEventId === invite.id}
                  onAccept={actions.onAcceptBreakfastInvite}
                  onDecline={actions.onDeclineBreakfastInvite}
                />
              ))}
              {popAtt.map((item) => (
                <AttendanceCard
                  key={item.date}
                  dateStr={item.date}
                  compact
                  isLoading={loading.attendanceDate === item.date}
                  onValidate={actions.onValidateAttendance}
                  onClear={actions.onClearAttendance}
                />
              ))}
              {popDb.map((notif) => (
                <NotifRow
                  key={notif.id}
                  notif={notif}
                  compact
                  inHistory={false}
                  sender={notif.sender_id ? senders.get(notif.sender_id) : undefined}
                  onRowClick={(id) => onOpenFullSheet(id)}
                  onMarkRead={actions.onMarkRead}
                  onArchive={actions.onArchive}
                  onDelete={(id) => actions.onDelete(id, false)}
                  onUnarchive={actions.onUnarchive}
                  onAcceptInvite={actions.onAcceptWorkspaceInvite}
                  onDeclineInvite={actions.onDeclineWorkspaceInvite}
                  onAcceptBuddy={actions.onAcceptBuddy}
                  onDeclineBuddy={actions.onDeclineBuddy}
                  onAcceptBreakfast={actions.onAcceptBreakfast}
                  onDeclineBreakfast={actions.onDeclineBreakfast}
                  onRemoveFromCalendar={actions.onRemoveFromCalendar}
                  onConfirmAttendance={(n) => {
                    actions.onValidateAttendance(n.created_at.slice(0, 10));
                    actions.onArchive(n.id);
                  }}
                  onClearAttendance={(n) => {
                    actions.onClearAttendance(n.created_at.slice(0, 10));
                    actions.onArchive(n.id);
                  }}
                />
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-border/40 bg-muted/20 flex items-center justify-between gap-2">
          <p className="text-[10px] text-muted-foreground">
            {hasPending ? "Check pending invites and days" : "All clear."}
          </p>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              onOpenFullSheet();
            }}
            className="text-[11px] font-semibold text-primary hover:text-primary/80 transition-colors whitespace-nowrap"
          >
            Open Notification Centre →
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
